import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";
import { ChevronLeft, User as UserIcon, Bell, Save, Moon, KeyRound } from "lucide-react";
import { ChangePasswordForm } from "../components/ChangePasswordForm.jsx";

const TYPE_LABELS = {
  overdue_task_l1: "Tarefa em atraso — Nível 1",
  overdue_task_l2: "Tarefa em atraso — Nível 2",
  overdue_task_l3: "Tarefa em atraso — Nível 3",
  overdue_task_l4: "Tarefa em atraso — Nível 4",
  overdue_task_l5: "Tarefa em atraso — Nível 5 (crítico)",
  feedback_48h: "Feedback do cliente (48h)",
  feedback_72h: "Feedback do cliente (72h crítico)",
  parts_expired_eta: "ETA de peças vencido",
  parts_received_no_contact: "Peças recebidas sem contacto",
  ready_for_delivery_no_appt: "Viatura pronta sem agendamento",
  warranty_incomplete: "Garantia com evidências em falta",
  critical_escalation_7d: "Escalonamento crítico (7+ dias)",
  mpm_expiring_30d: "Plano MPM a expirar em 30d",
  mpm_expiring_60d: "Plano MPM a expirar em 60d",
  mpm_expiring_90d: "Plano MPM a expirar em 90d",
};

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users/me/notification-prefs")
      .then((r) => setData(r.data))
      .catch((e) => setLoadError(e?.response?.data?.detail || "Não foi possível carregar as preferências."));
  }, []);

  const toggleMute = (k) => {
    const muted = new Set(data.prefs.muted_types);
    if (muted.has(k)) muted.delete(k); else muted.add(k);
    setData({ ...data, prefs: { ...data.prefs, muted_types: [...muted].sort() } });
  };

  const updateField = (key, value) => {
    setData({ ...data, prefs: { ...data.prefs, [key]: value } });
  };

  const updateQuiet = (patch) => {
    setData({ ...data, prefs: { ...data.prefs, quiet_hours: { ...data.prefs.quiet_hours, ...patch } } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: fresh } = await api.put("/users/me/notification-prefs", { prefs: data.prefs });
      setData(fresh);
      toast.success("Preferências guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!data && !loadError) return <div className="p-6 text-sm text-[#6C757D]" data-testid="profile-loading">A carregar…</div>;

  const prefs = data?.prefs;
  const available = data?.available_types || [];

  return (
    <div className="p-4 sm:p-6 max-w-[900px] mx-auto" data-testid="profile-page">
      <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
        <Link to="/" className="flex items-center gap-1 hover:text-[#1F3864]">
          <ChevronLeft className="w-3 h-3" /> Painel
        </Link>
        <span>/</span><span>Meu perfil</span>
      </div>

      <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2 mb-1">
        <UserIcon className="w-6 h-6 text-[#1F3864]" /> {user?.name || "—"}
      </h1>
      <div className="text-sm text-[#6C757D] mb-6">
        <span className="font-mono text-xs">{user?.email}</span> · {(user?.role || "").replaceAll("_", " ")}
      </div>

      <section className="bg-white border border-gray-200 rounded-sm p-4 mb-4" data-testid="profile-security-section">
        <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1 flex items-center gap-1.5">
          <KeyRound className="w-4 h-4 text-[#1F3864]" /> Segurança — alterar palavra-passe
        </h2>
        <p className="text-xs text-[#6C757D] mb-3">Introduza a palavra-passe actual e escolha uma nova.</p>
        <ChangePasswordForm onSuccess={(fresh) => { setUser(fresh); toast.success("Palavra-passe alterada com sucesso"); }} />
      </section>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-sm p-3" data-testid="profile-load-error">{loadError}</div>
      )}
      {prefs && (<>

      {/* Alternate email */}
      <section className="bg-white border border-gray-200 rounded-sm p-4 mb-4">
        <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1 flex items-center gap-1.5">
          <Bell className="w-4 h-4 text-[#1F3864]" /> Email para notificações
        </h2>
        <p className="text-xs text-[#6C757D] mb-3">
          Se preencheres um email diferente, as notificações do sistema vão para este endereço em vez do email de login.
        </p>
        <input
          type="email"
          value={prefs.notification_email}
          onChange={(e) => updateField("notification_email", e.target.value)}
          placeholder={user?.email || "email@dominio.com"}
          className="w-full max-w-md px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864]"
          data-testid="profile-notification-email"
        />
      </section>

      {/* Quiet hours */}
      <section className="bg-white border border-gray-200 rounded-sm p-4 mb-4">
        <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1 flex items-center gap-1.5">
          <Moon className="w-4 h-4 text-[#1F3864]" /> Horas de silêncio
        </h2>
        <p className="text-xs text-[#6C757D] mb-3">
          Durante este intervalo não recebes notificações in-app. Emails continuam a ser enviados normalmente.
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={prefs.quiet_hours.enabled}
              onChange={(e) => updateQuiet({ enabled: e.target.checked })}
              className="w-4 h-4 accent-[#1F3864]"
              data-testid="profile-quiet-toggle"
            />
            <span className="text-sm">Activar</span>
          </label>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#6C757D]">Início</label>
            <input
              type="time"
              value={prefs.quiet_hours.start}
              onChange={(e) => updateQuiet({ start: e.target.value })}
              disabled={!prefs.quiet_hours.enabled}
              className="px-2 py-1 text-sm border border-gray-300 rounded-sm disabled:opacity-50 font-mono"
              data-testid="profile-quiet-start"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#6C757D]">Fim</label>
            <input
              type="time"
              value={prefs.quiet_hours.end}
              onChange={(e) => updateQuiet({ end: e.target.value })}
              disabled={!prefs.quiet_hours.enabled}
              className="px-2 py-1 text-sm border border-gray-300 rounded-sm disabled:opacity-50 font-mono"
              data-testid="profile-quiet-end"
            />
          </div>
        </div>
      </section>

      {/* Muted types */}
      <section className="bg-white border border-gray-200 rounded-sm p-4 mb-4">
        <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-1">Silenciar tipos de notificação</h2>
        <p className="text-xs text-[#6C757D] mb-3">
          Notificações silenciadas não aparecem nem no email nem no sino. As de níveis críticos (L5, escalonamentos, 72h feedback) devem ficar sempre activas para não perderes eventos importantes.
        </p>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {available.map((k) => {
            const muted = prefs.muted_types.includes(k);
            return (
              <label
                key={k}
                className={`flex items-center gap-2 px-2 py-1.5 border rounded-sm cursor-pointer ${
                  muted ? "border-[#DC3545]/40 bg-[#FDF2F2]" : "border-gray-200 hover:border-[#1F3864]"
                }`}
                data-testid={`profile-mute-${k}`}
              >
                <input
                  type="checkbox"
                  checked={muted}
                  onChange={() => toggleMute(k)}
                  className="w-3.5 h-3.5 accent-[#DC3545]"
                />
                <span className="text-xs text-[#1A1A2E]">{TYPE_LABELS[k] || k}</span>
              </label>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 text-sm rounded-sm bg-[#1F3864] text-white hover:bg-[#142542] disabled:opacity-60 flex items-center gap-1.5"
          data-testid="profile-save"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "A guardar…" : "Guardar preferências"}
        </button>
      </div>
      </>)}
    </div>
  );
}
